/**
 * Hook to register Connection (Quick Links) tab with DetailPageTabsContext
 * 
 * This hook automatically adds the Connection tab to any doctype detail page.
 * It's meta-driven - works for all doctypes without additional configuration.
 * 
 * Best Practices:
 * - Uses functional updates for setTabs (prev => ...) to avoid dependency issues
 * - No dependency on tabs array (prevents loops/glitches)
 * - Automatically manages tab lifecycle (add/remove based on conditions)
 * - Uses constant for tab ID to ensure consistency
 */

import { useEffect, useRef } from 'react';
import { useDetailPageTabs } from '@/contexts/DetailPageTabsContext';
import type { TabStructure } from '@/features/inventory/item-detail/utils/buildTabsFromMeta';
import { CONNECTIONS_TAB_ID } from './useConnectionsTab';

/**
 * Tab ID constant - use this everywhere you reference the Connection tab
 */
export const QUICK_CONNECTIONS_TAB_ID = 'connection';

interface UseQuickConnectionsTabOptions {
  doctype: string;
  name?: string;
  enabled?: boolean; // Only show for existing documents
}

/**
 * Hook to register Connection tab with DetailPageTabsContext.
 * 
 * This makes the Connection tab available in the tab navigation
 * for any doctype detail page.
 * 
 * The tab is automatically:
 * - Added when document has a name (not new)
 * - Removed when document is new
 * - Positioned before the "System Connections" tab
 * 
 * Uses functional updates to prevent dependency issues and tab duplication.
 */
export function useQuickConnectionsTab({ 
  doctype, 
  name, 
  enabled = true 
}: UseQuickConnectionsTabOptions) {
  const { setTabs, tabs: contextTabs } = useDetailPageTabs();
  
  useEffect(() => {
    const shouldShow = enabled && !!doctype && !!name && name !== 'new';
    
    
    setTabs((prevTabs: TabStructure[]) => {
      const exists = prevTabs.some(t => t.name === QUICK_CONNECTIONS_TAB_ID);
      const currentIdx = exists ? prevTabs.findIndex(t => t.name === QUICK_CONNECTIONS_TAB_ID) : -1;
      const connectionsIdx = prevTabs.findIndex(t => t.name === CONNECTIONS_TAB_ID);
      
      // Create the Connection tab structure
      const connectionTab: TabStructure = {
        name: QUICK_CONNECTIONS_TAB_ID,
        label: 'Connection', // Will be translated via getTabLabelTranslation
        sections: [], // Empty sections - this tab is handled separately in SystemView/ExperienceView
      };
      
      // If tab should show
      if (shouldShow) {
        // If tab doesn't exist, add it
        if (!exists) {
          // Insert before "System Connections" tab if it exists
          if (connectionsIdx !== -1) {
            const newTabs = [...prevTabs];
            newTabs.splice(connectionsIdx, 0, connectionTab);
            return newTabs;
          }
          
          // If Connections tab doesn't exist yet, append at end
          return [...prevTabs, connectionTab];
        }
        
        // Tab exists - ensure it's in the correct position and has correct properties
        if (exists && currentIdx !== -1) {
          const currentTab = prevTabs[currentIdx];
          let needsUpdate = false;
          const newTabs = [...prevTabs];
          
          // Check if label is correct
          if (currentTab.label !== 'Connection') {
            newTabs[currentIdx] = { ...currentTab, label: 'Connection' };
            needsUpdate = true;
          }
          
          // Check if position is correct (should be before Connections tab)
          if (connectionsIdx !== -1 && currentIdx > connectionsIdx) {
            // Move Connection tab before Connections tab
            const [tab] = newTabs.splice(currentIdx, 1);
            newTabs.splice(connectionsIdx, 0, tab);
            needsUpdate = true;
          }
          
          if (needsUpdate) {
            return newTabs;
          }
        }
        
        // Tab exists and is in correct position - no change needed
        return prevTabs;
      }
      
      // Tab should not show - remove it if it exists
      if (!shouldShow && exists) {
        return prevTabs.filter(t => t.name !== QUICK_CONNECTIONS_TAB_ID);
      }
      
      // No change needed
      return prevTabs;
    });
  }, [doctype, name, enabled, setTabs]);
  
  // Separate effect to ensure tab persists even if removed by other effects (like TabRenderer)
  // This acts as a "watchdog" that re-adds the tab if it disappears
  // Use a ref to track if we've already registered to avoid infinite loops
  const hasTabRef = useRef(false);
  
  useEffect(() => {
    const shouldShow = enabled && !!doctype && !!name && name !== 'new';
    
    if (!shouldShow) {
      hasTabRef.current = false;
      return;
    }
    
    // Check if tab exists in current context
    const exists = contextTabs.some(t => t.name === QUICK_CONNECTIONS_TAB_ID);
    
    // Only act if state changed (tab appeared or disappeared)
    if (exists !== hasTabRef.current) {
      hasTabRef.current = exists;
      
      if (!exists) {
        // Tab was removed - re-add it
        setTabs((prevTabs: TabStructure[]) => {
          // Double-check it's still missing (race condition protection)
          if (prevTabs.some(t => t.name === QUICK_CONNECTIONS_TAB_ID)) {
            hasTabRef.current = true;
            return prevTabs;
          }
          
          const connectionTab: TabStructure = {
            name: QUICK_CONNECTIONS_TAB_ID,
            label: 'Connection',
            sections: [],
          };
          
          const connectionsIdx = prevTabs.findIndex(t => t.name === CONNECTIONS_TAB_ID);
          if (connectionsIdx !== -1) {
            const newTabs = [...prevTabs];
            newTabs.splice(connectionsIdx, 0, connectionTab);
            return newTabs;
          }
          
          return [...prevTabs, connectionTab];
        });
      }
    }
  }, [doctype, name, enabled, contextTabs, setTabs]); // Watch contextTabs to detect when tab disappears
  
  return {
    tabId: QUICK_CONNECTIONS_TAB_ID,
    isEnabled: enabled && !!name && name !== 'new' && !!doctype,
  };
}

